'use client';
import {
  arrangeFiltersByOrder,
  checkIsFJFeatureAllowed,
} from '@components/common';
import SearchTipsFilter from '@components/remoteJob/searchTipsFilter';
import {
  CountryResponseProps,
  ICompanies,
  IDeviceType,
  IJob,
  SubCategories,
  SuccessStory,
} from '@components/shared.types';
import { width } from '@styles/device.styled';
import useMediaQuery from '@utils/useMediaQuery';
import { isRCPortal } from 'helper';
import omit from 'lodash/omit';
import { memo, useEffect, useRef, useState } from 'react';
import { Wrapper } from './styled';

type FiltersDataByOrderType = {
  name: string;
  categoryWithCount: CategoryWithCount;
  slug: string;
};

type CategoryWithCount = {
  [key: string]: number;
};

interface IProps {
  selectedCountry?: string;
  categorizedJobs?: any;
  isForCategoryWithLocation?: boolean;
  skipSubCategoryTitle?: boolean;
  isCategoryJobsPage?: boolean;
  categoryTitle?: any;
  pageName?: string;
  isPremiumUser: boolean;
  showJobsCount?: boolean;
  localization: any;
  jobsData: any;
  savedJobsIdsList?: any;
  location?: any;
  isLoggedIn?: any;
  sectionsToHide?: any;
  jobFilters?: any;
  widgetsName?: any;
  newsData?: any;
  successStoriesList?: any;
  categoryDetails?: any;
  queryParamsByOrder?: any;
  jobSubCategories?: SubCategories;
  faqData?: any;
  categorySuccessStories?: [SuccessStory];
  mobileView?: boolean;
  searchedJobTitle?: string;
  selectedFilters?: { [key: string]: Array<string> };
  isForLocation?: boolean;
  pageTitle?: string;
  pageLocation?: string;
  breadCrumbName?: string;
  breadCrumbNameValue?: Array<string>;
  breadcrumbDropdown?: any;
  showWorkAnywhereInUsCheckbox?: boolean;
  showWorkAnywhereInWorldCheckbox?: boolean;
  isCategorySubCategory?: boolean;
  deviceType?: IDeviceType;
  moreDropdownText?: string;
  moreJobs?: any;
  tabsToHide?: string[];
  countyList?: CountryResponseProps[];
  openSearchOnLocationChange?: boolean;
  showAllDropFilters?: boolean;
  isSearchPage?: boolean;
  saveSeachButtonState?: string;
  guid?: string;
  setGuid?: Function | undefined;
  isWorkAnywhereInWorldChecked?: boolean;
  isWorkAnywhereInUsChecked?: boolean;
  firstJobDetails?: IJob;
  firstJobComDetails?: ICompanies;
  hideJobCount?: boolean;
  showH1Heading?: boolean;
  hideJobCard?: boolean;
  topBanner?: {
    showBanner: boolean;
    bannerTitle?: string;
    bannerImage?: string;
  };
  isRemoteJobsPage?: boolean;
  isRCPortal?: boolean;
  isCompanyComponent?: boolean;
  isCategoryPage?: boolean;
  companyName?: string;
  isLegitimate?: boolean;
  overviewText?: string;
  queryParams?: any;
  isBasealign?: boolean;
  isAddStaticSearchList?: boolean;
  envPath?: string;
  themeType?: string;
  isExpSavedSearchDropDown?: boolean;
}

const SearchListingContainer = (props: IProps) => {
  const {
    localization,
    jobsData,
    location,
    isPremiumUser,
    isLoggedIn,
    deviceType,
    countyList,
    isSearchPage,
    isLegitimate,
    queryParams,
    isRemoteJobsPage,
    isBasealign,
    isAddStaticSearchList,
    envPath,
    themeType,
    isExpSavedSearchDropDown,
    isCategoryJobsPage,
  } = props;

  const isRMC = checkIsFJFeatureAllowed();
  const isMobileView: boolean =
    useMediaQuery(width.tablet) || deviceType === 'mobile';
  const [isOpenDetailJobId, setDetailJobId] = useState<string>(
    Array.isArray(jobsData?.jobs?.results) &&
      jobsData?.jobs?.results.length > 0 &&
      isRMC &&
      isPremiumUser
      ? jobsData.jobs.results[0]?.id
      : ''
  );
  const [togglePopOver, setTogglePopOver] = useState<boolean>(false);
  const [showJobDetailPopup, setShowJobDetailPopup] = useState<boolean>(false);
  const mainWrapperRef = useRef<HTMLDivElement>(null!);
  const dateButtonRef = useRef<HTMLDivElement>(null!);
  const relevanceButtonRef = useRef<HTMLDivElement>(null!);

  const paginationData = omit(jobsData?.jobs, 'results');

  const hideDetails = () => {
    !isMobileView && setDetailJobId('');
  };

  const hidePopover = (e: any) => {
    if (togglePopOver && !e.target.classList.contains('fa-info-circle')) {
      setTogglePopOver(false);
    }
  };

  useEffect(() => {
    if (togglePopOver) {
      document.addEventListener('click', hidePopover);
    } else {
      document.removeEventListener('click', hidePopover);
    }
    return () => {
      document.removeEventListener('click', hidePopover);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [togglePopOver]);

  useEffect(() => {
    const handlePopState = () => {
      setShowJobDetailPopup(false);
    };

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMobileView, showJobDetailPopup]);

  // * if user not premieum remove company filter
  let searchFilters = props.jobFilters;
  if (!props.isPremiumUser && Array.isArray(props.jobFilters)) {
    searchFilters = props.jobFilters.filter(
      (filter: any) => filter.name !== 'Company'
    );
  }

  let queryParamsByOrder: {
    [key: string]: Array<string> | string | undefined;
  } = arrangeFiltersByOrder(queryParams);

  let jobFilterDataByOrder: Array<FiltersDataByOrderType> =
    arrangeFiltersByOrder(searchFilters || [], true, queryParams, deviceType);

  const searchTipsFilter = () => {
    return (
      <SearchTipsFilter
        isAddStaticSearchList={isAddStaticSearchList}
        jobFilters={jobFilterDataByOrder}
        paginationData={paginationData}
        queryParams={queryParamsByOrder}
        location={location}
        searchedJobTitle={props.searchedJobTitle}
        selectedFilters={props.selectedFilters}
        localization={localization}
        isPremiumUser={props.isPremiumUser}
        showJobsCount={props.showJobsCount}
        deviceType={deviceType}
        selectedCountry={props.selectedCountry}
        countyList={countyList}
        categoryDetails={props.categoryDetails}
        openSearchOnLocationChange={props.openSearchOnLocationChange}
        showAllDropFilters={props.showAllDropFilters}
        jobsData={jobsData}
        setGuid={props.setGuid}
        guid={props.guid}
        saveSeachButtonState={props.saveSeachButtonState}
        mainWrapperRef={mainWrapperRef}
        hideDetails={hideDetails}
        dateButtonRef={dateButtonRef}
        relevanceButtonRef={relevanceButtonRef}
        hideJobCount={props.hideJobCount}
        isSearchPage={isSearchPage}
        isRemoteJobsPage={isRemoteJobsPage}
        isRCPortal={isRCPortal}
        isOpenDetailJobId={isOpenDetailJobId}
        isLoggedIn={isLoggedIn}
        isBasealign={isBasealign}
        envPath={envPath}
        themeType={themeType}
        isExpSavedSearchDropDown={isExpSavedSearchDropDown}
        isCategoryJobsPage={isCategoryJobsPage}
      />
    );
  };

  return (
    <Wrapper isSearchPage={props.isSearchPage} isLegitimate={isLegitimate}>
      {/* search Filter */}
      {searchTipsFilter()}
    </Wrapper>
  );
};

export default memo(SearchListingContainer);
